import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { normalizeImageUrl } from "@/lib/imageUrl";
import SchoolLogo from "@/components/SchoolLogo";
import { FreeProfileCTA, WWBAInterestCTA } from "@/components/interest/ProfileCTAs";
import { Search, Star, GraduationCap, Trophy, ChevronRight, User } from "lucide-react";

const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];
const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];
const inp = "w-full px-3 py-2 border border-white/15 bg-white/5 rounded-sm text-sm text-white placeholder:text-white/40 focus:border-[#E63946] outline-none";

export default function CBRNationAlumni() {
  const [items, setItems] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", graduation_year: "", primary_position: "", province: "", college: false, drafted: false, professional: false });

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== false && v !== null) params[k] = v; });
    try {
      const { data } = await api.get("/alumni", { params });
      setItems(data.items || []); setFeatured(data.featured || []);
    } catch { setItems([]); setFeatured([]); } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const Toggle = ({ k, label }) => (
    <button data-testid={`alumni-filter-${k}`} onClick={() => setFilters({ ...filters, [k]: !filters[k] })}
      className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm border ${filters[k] ? "bg-[#E63946] border-[#E63946] text-white" : "border-white/20 text-white/70"}`}>{label}</button>
  );

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-[#0A192F] text-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="label-overline text-[#E63946]">CBR Nation</div>
          <h1 data-testid="alumni-hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mt-3">CBR Nation Alumni</h1>
          <p className="text-base text-white/70 mt-4 max-w-2xl">Celebrating the players who represented the Nation and continued their baseball journey at the collegiate, national and professional levels.</p>

          {/* Filters */}
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative sm:col-span-2 lg:col-span-1">
              <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="alumni-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search name…" className={`${inp} pl-9`} />
            </div>
            <input data-testid="alumni-grad" value={filters.graduation_year} onChange={(e) => setFilters({ ...filters, graduation_year: e.target.value })} placeholder="Grad year" className={inp} />
            <select data-testid="alumni-position" value={filters.primary_position} onChange={(e) => setFilters({ ...filters, primary_position: e.target.value })} className={`${inp} bg-[#0A192F]`}><option value="">All positions</option>{POSITIONS.map((p) => <option key={p}>{p}</option>)}</select>
            <select data-testid="alumni-province" value={filters.province} onChange={(e) => setFilters({ ...filters, province: e.target.value })} className={`${inp} bg-[#0A192F]`}><option value="">All provinces</option>{PROVINCES.map((p) => <option key={p}>{p}</option>)}</select>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Toggle k="college" label="College Committed" />
            <Toggle k="drafted" label="Drafted" />
            <Toggle k="professional" label="Professional" />
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-10 space-y-4">
          <WWBAInterestCTA />
          <FreeProfileCTA />
        </div>
        {/* Featured */}
        {featured.length > 0 && (
          <section className="mb-12" data-testid="alumni-featured">
            <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2"><Star className="w-5 h-5 text-[#E63946]" /> Featured Alumni</h2>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featured.map((a) => <AlumniCard key={a.id} a={a} featured />)}
            </div>
          </section>
        )}

        <h2 className="font-display text-2xl uppercase tracking-tight">All Alumni {loading ? "" : `(${items.length})`}</h2>
        {loading ? <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
          : items.length === 0 ? <div className="py-16 text-center text-sm text-slate-400" data-testid="alumni-empty">No alumni match these filters yet.</div>
          : <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="alumni-grid">{items.map((a) => <AlumniCard key={a.id} a={a} />)}</div>}
      </div>
    </PublicLayout>
  );
}

function AlumniCard({ a, featured }) {
  const status = a.professional_organization ? { label: a.professional_organization, tone: "bg-[#E63946] text-white" }
    : a.drafted_organization ? { label: `Drafted · ${a.drafted_organization}`, tone: "bg-amber-500 text-white" }
    : a.commitment_school ? { label: `Committed · ${a.commitment_school}`, tone: "bg-emerald-600 text-white" }
    : a.current_team_school ? { label: a.current_team_school, tone: "bg-slate-200 text-slate-700" } : null;
  return (
    <Link to={`/prospects/${a.slug || a.id}`} data-testid={`alumni-card-${a.id}`}
      className={`block bg-white border rounded-sm overflow-hidden hover:shadow-lg transition-shadow group ${featured ? "border-[#E63946]" : "border-slate-200"}`}>
      <div className="flex">
        <div className="w-24 h-28 shrink-0 bg-slate-100 grid place-items-center overflow-hidden">
          {a.profile_photo_url ? <img src={normalizeImageUrl(a.profile_photo_url)} alt="" className="w-full h-full object-cover" /> : <User className="w-8 h-8 text-slate-300" />}
        </div>
        <div className="p-3 flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="font-display text-lg uppercase tracking-tight leading-tight group-hover:text-[#E63946]">{a.first_name} {a.last_name}</div>
            {featured && <Star className="w-4 h-4 text-[#E63946] shrink-0" />}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {[a.primary_position, a.secondary_position].filter(Boolean).join("/")}{a.graduation_year ? ` · ${a.graduation_year}` : ""}{a.province ? ` · ${a.province}` : ""}
          </div>
          {a.cbr_nation_team_year && <div className="text-[11px] text-slate-600 mt-1 flex items-center gap-1"><GraduationCap className="w-3 h-3 text-slate-400" /> {a.cbr_nation_team_year}</div>}
          {status && (
            <span className={`inline-flex items-center gap-1 mt-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${status.tone}`}>
              {a.commitment_logo_url && !a.professional_organization && !a.drafted_organization && (
                <SchoolLogo name={a.commitment_school} logoUrl={a.commitment_logo_url} size="sm" className="!w-3.5 !h-3.5 !border-0 !bg-transparent" />
              )}
              {status.label}
            </span>
          )}
          {a.alumni_accomplishment && <div className="text-[11px] text-slate-500 mt-2 flex items-start gap-1"><Trophy className="w-3 h-3 text-amber-500 shrink-0 mt-0.5" /> {a.alumni_accomplishment}</div>}
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#E63946] mt-2 flex items-center gap-1">View Profile <ChevronRight className="w-3 h-3" /></div>
        </div>
      </div>
    </Link>
  );
}
